import * as React from 'react';
import {
  Header,
  Segment,
  Message,
  Table,
  Label,
  Loader,
  Input,
  Button
} from 'semantic-ui-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import useCsrfFetchJson from './hooks/use_csrf_fetch';
import MudColoredText from './utils/mud_colors';
import OocChat from './ooc_chat';
import './mud.css';

const useQueryClient = () => {
  return React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }))[0];
};

const LoadingState = ({ text }) => (
  <Loader active inline="centered">{text}</Loader>
);

const ErrorState = ({ text, error }) => (
  <Message negative>
    <Message.Header>{text}</Message.Header>
    <p>{error?.message}</p>
  </Message>
);

const CharactersSection = ({ selectedId, onSelect, showEmptyState }) => {
  const fetchJson = useCsrfFetchJson();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'characters'],
    queryFn: () => fetchJson('/player/characters/mine'),
  });
  const { data: details, isLoading: isDetailsLoading, isError: isDetailsError, error: detailsError } = useQuery({
    queryKey: ['player', 'character_details', selectedId],
    queryFn: () => fetchJson(`/player/characters/${selectedId}/details`),
    enabled: !!selectedId,
  });
  const [otpMessage, setOtpMessage] = React.useState(null);

  if (isLoading) return <LoadingState text="Loading characters..." />;
  if (isError) return <ErrorState text="Unable to load characters" error={error} />;
  if (!data.length) {
    return (
      <div className="lots-card lots-card--accent">
        <div className="portal-kicker">No characters found</div>
        <h3>Start your first story arc</h3>
        <p>Create your first character to begin exploring the galaxy.</p>
        <div className="portal-cta-row">
          <a className="lots-btn" href="/character/new">Create Character</a>
          <a className="lots-btn lots-btn--ghost" href="/mud">Play Online (alpha)</a>
        </div>
      </div>
    );
  }

  const handleCopyOtp = async (characterId) => {
    try {
      const response = await fetchJson(`/player/characters/${characterId}/otp`);
      if (response?.otp) {
        await navigator.clipboard.writeText(response.otp);
        setOtpMessage(`OTP copied. Expires ${response.expires_at ? new Date(response.expires_at).toLocaleTimeString() : 'soon'}.`);
      }
    } catch (err) {
      setOtpMessage('Unable to copy OTP.');
    }
  };

  const handleRotateOtp = async (characterId) => {
    try {
      const response = await fetchJson(`/player/characters/${characterId}/rotate_otp`, { method: 'POST' });
      if (response?.otp) {
        await navigator.clipboard.writeText(response.otp);
        setOtpMessage(`OTP rotated and copied. Expires ${response.expires_at ? new Date(response.expires_at).toLocaleTimeString() : 'soon'}.`);
      }
    } catch (err) {
      setOtpMessage('Unable to rotate OTP.');
    }
  };

  return (
    <>
      {otpMessage && (
        <div className="portal-highlight" onClick={() => setOtpMessage(null)}>
          {otpMessage}
        </div>
      )}
      {showEmptyState && !selectedId && (
        <div className="lots-card">
          <div className="portal-kicker">Welcome back</div>
          <h3>Choose your character</h3>
          <p>Select an existing character or start a new one.</p>
          <div className="portal-cta-row">
            <a className="lots-btn lots-btn--ghost" href="#characters">Select Existing</a>
            <a className="lots-btn" href="/character/new">Create New</a>
          </div>
        </div>
      )}
      <Table celled striped selectable className="portal-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Level</Table.HeaderCell>
            <Table.HeaderCell>Race</Table.HeaderCell>
            <Table.HeaderCell>Class</Table.HeaderCell>
            <Table.HeaderCell>Faction</Table.HeaderCell>
            <Table.HeaderCell>Online</Table.HeaderCell>
            <Table.HeaderCell>OTP</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((character) => (
            <Table.Row
              key={character.id}
              active={selectedId === character.id}
              onClick={() => onSelect(character.id)}
            >
              <Table.Cell>
                <a href={`#characters/${character.id}`}>{character.name}</a>
              </Table.Cell>
              <Table.Cell>{character.top_level || '-'}</Table.Cell>
              <Table.Cell>{character.race || '-'}</Table.Cell>
              <Table.Cell>{character.main_ability || '-'}</Table.Cell>
              <Table.Cell>{character.current_faction || '-'}</Table.Cell>
              <Table.Cell>
                {character.is_online ? <Label color="green">Online</Label> : <Label>Offline</Label>}
              </Table.Cell>
              <Table.Cell>
                <Button
                  size="tiny"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyOtp(character.id);
                  }}
                >
                  Copy OTP
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {selectedId && isDetailsLoading && (
        <LoadingState text="Loading character details..." />
      )}
      {selectedId && isDetailsError && (
        <ErrorState text="Unable to load character details" error={detailsError} />
      )}
      {details && (
        <div className="portal-section">
          <div className="portal-kicker">Selected character</div>
          <h3>{details.name}</h3>
          <div className="portal-cta-row">
            <button
              type="button"
              className="lots-btn"
              onClick={() => handleCopyOtp(details.id)}
            >
              Copy OTP to play
            </button>
            <button
              type="button"
              className="lots-btn lots-btn--ghost"
              onClick={() => handleRotateOtp(details.id)}
            >
              Rotate OTP
            </button>
          </div>
          <div className="lots-grid lots-grid--two" style={{ marginTop: '1rem' }}>
            <div className="portal-chip">Race: {details.race || '-'}</div>
            <div className="portal-chip">Class: {details.main_ability || '-'}</div>
            <div className="portal-chip">Level: {details.top_level || '-'}</div>
            <div className="portal-chip">Faction: {details.current_faction || '-'}</div>
            <div className="portal-chip">Status: {details.is_online ? 'Online' : 'Offline'}</div>
          </div>
          <p style={{ marginTop: '1rem' }}>
            <strong>Title:</strong>{' '}
            <span className="mud-inline"><MudColoredText text={`&w${details.title || ''}&w`} /></span>
          </p>
          <Header as="h5">Description</Header>
          <div className="mud">
            <MudColoredText text={`&w${details.description || ''}&w`} />
          </div>
          <Header as="h5">Biography</Header>
          <div className="mud">
            <MudColoredText text={`&w${details.biography || ''}&w`} />
          </div>
        </div>
      )}
    </>
  );
};

const FactionsSection = ({ selectedId, onSelect }) => {
  const fetchJson = useCsrfFetchJson();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'factions'],
    queryFn: () => fetchJson('/player/factions/list'),
  });
  const { data: details, isLoading: isDetailsLoading, isError: isDetailsError, error: detailsError } = useQuery({
    queryKey: ['player', 'faction_details', selectedId],
    queryFn: () => fetchJson(`/player/factions/${selectedId}/details`),
    enabled: !!selectedId,
  });

  if (isLoading) return <LoadingState text="Loading factions..." />;
  if (isError) return <ErrorState text="Unable to load factions" error={error} />;

  return (
    <>
      <Table celled striped selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Name</Table.HeaderCell>
            <Table.HeaderCell>Short Name</Table.HeaderCell>
            <Table.HeaderCell>Acronym</Table.HeaderCell>
            <Table.HeaderCell>Leader</Table.HeaderCell>
            <Table.HeaderCell>Type</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((faction) => (
            <Table.Row
              key={faction.id}
              active={selectedId === faction.id}
              onClick={() => onSelect(faction.id)}
            >
              <Table.Cell>
                <a href={`#factions/${faction.id}`}>{faction.name}</a>
              </Table.Cell>
              <Table.Cell>{faction.short_name || '-'}</Table.Cell>
              <Table.Cell>{faction.acro || '-'}</Table.Cell>
              <Table.Cell>{faction.leader || '-'}</Table.Cell>
              <Table.Cell>{faction.faction_type || '-'}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {selectedId && isDetailsLoading && (
        <LoadingState text="Loading faction details..." />
      )}
      {selectedId && isDetailsError && (
        <ErrorState text="Unable to load faction details" error={detailsError} />
      )}
      {details && (
        <Segment>
          <Header as="h4">{details.name}</Header>
          <p><strong>Leader:</strong> {details.leader || '-'}</p>
          <p><strong>Description:</strong> {details.description || '-'}</p>
          <Header as="h5">Planets</Header>
          {details.planets?.length ? (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Planet</Table.HeaderCell>
                  <Table.HeaderCell>Governor</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {details.planets.map((planet) => (
                  <Table.Row key={planet.id}>
                    <Table.Cell>{planet.name}</Table.Cell>
                    <Table.Cell>{planet.governor || '-'}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <p>No governed planets.</p>
          )}
        </Segment>
      )}
    </>
  );
};

const ForceAppsSection = () => {
  const fetchJson = useCsrfFetchJson();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'force_applications'],
    queryFn: () => fetchJson('/player/force_applications/mine'),
  });

  if (isLoading) return <LoadingState text="Loading force applications..." />;
  if (isError) return <ErrorState text="Unable to load force applications" error={error} />;

  return (
    <Table celled striped>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Character</Table.HeaderCell>
          <Table.HeaderCell>Race</Table.HeaderCell>
          <Table.HeaderCell>Class</Table.HeaderCell>
          <Table.HeaderCell>Approved</Table.HeaderCell>
          <Table.HeaderCell>Created</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((app) => (
          <Table.Row key={app.id}>
            <Table.Cell>
              <a href={`/player/force_applications/${app.id}`}>{app.character_name}</a>
            </Table.Cell>
            <Table.Cell>{app.race || '-'}</Table.Cell>
            <Table.Cell>{app.ability_class || '-'}</Table.Cell>
            <Table.Cell>{app.is_approved ? 'Yes' : 'No'}</Table.Cell>
            <Table.Cell>{app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

const HelpsSection = () => {
  const fetchJson = useCsrfFetchJson();
  const [filter, setFilter] = React.useState('');
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'helps'],
    queryFn: () => fetchJson('/player/helps/list'),
  });

  if (isLoading) return <LoadingState text="Loading helps..." />;
  if (isError) return <ErrorState text="Unable to load helps" error={error} />;

  const filtered = data.filter((help) =>
    help.keyword?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <Input
        icon="search"
        placeholder="Filter helps..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: '1rem' }}
      />
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Keyword</Table.HeaderCell>
            <Table.HeaderCell>Text</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {filtered.map((help) => (
            <Table.Row key={help.id}>
              <Table.Cell>
                <a href={`/player/helps/${help.id}`}>{help.keyword}</a>
              </Table.Cell>
              <Table.Cell className="mud">
                <MudColoredText text={`&w${help.mud_text || ''}&w`} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

const RpLogsSection = () => {
  const fetchJson = useCsrfFetchJson();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'rp_logs'],
    queryFn: () => fetchJson('/player/role_play_logs/mine'),
  });
  const [selectedId, setSelectedId] = React.useState(null);

  if (isLoading) return <LoadingState text="Loading role play logs..." />;
  if (isError) return <ErrorState text="Unable to load role play logs" error={error} />;

  const selected = data.find((log) => log.id === selectedId) || data[0];

  return (
    <>
      <Table celled striped selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell>Character</Table.HeaderCell>
            <Table.HeaderCell>Lines</Table.HeaderCell>
            <Table.HeaderCell>Awarded</Table.HeaderCell>
            <Table.HeaderCell>Created</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((log) => (
            <Table.Row
              key={log.id}
              active={selected && log.id === selected.id}
              onClick={() => setSelectedId(log.id)}
            >
              <Table.Cell>
                <a href={`/player/role_play_logs/${log.id}`}>{log.title}</a>
              </Table.Cell>
              <Table.Cell>{log.character || '-'}</Table.Cell>
              <Table.Cell>{log.line_count}</Table.Cell>
              <Table.Cell>{log.awarded ? 'Yes' : 'No'}</Table.Cell>
              <Table.Cell>{log.created_at ? new Date(log.created_at).toLocaleDateString() : '-'}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {selected && (
        <div className="mud">
          <Header as="h4">{selected.title}</Header>
          <MudColoredText text={`&w${selected.full_text || ''}&w`} />
        </div>
      )}
    </>
  );
};

const RpRequestsSection = () => {
  const fetchJson = useCsrfFetchJson();
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'rp_requests'],
    queryFn: () => fetchJson('/player/role_play_requests/mine'),
  });

  if (isLoading) return <LoadingState text="Loading role play requests..." />;
  if (isError) return <ErrorState text="Unable to load role play requests" error={error} />;

  return (
    <Table celled striped>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Title</Table.HeaderCell>
          <Table.HeaderCell>Character</Table.HeaderCell>
          <Table.HeaderCell>Type</Table.HeaderCell>
          <Table.HeaderCell>Approved</Table.HeaderCell>
          <Table.HeaderCell>Created</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data.map((req) => (
          <Table.Row key={req.id}>
            <Table.Cell>
              <a href={`/player/role_play_requests/${req.id}`}>{req.title}</a>
            </Table.Cell>
            <Table.Cell>{req.character || '-'}</Table.Cell>
            <Table.Cell>{req.request_type || '-'}</Table.Cell>
            <Table.Cell>{req.is_approved ? 'Yes' : 'No'}</Table.Cell>
            <Table.Cell>{req.created_at ? new Date(req.created_at).toLocaleDateString() : '-'}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

const PlanetsSection = () => {
  const fetchJson = useCsrfFetchJson();
  const [filter, setFilter] = React.useState('');
  const [sortKey, setSortKey] = React.useState('name');
  const [sortDir, setSortDir] = React.useState('ascending');
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['player', 'planets'],
    queryFn: () => fetchJson('/player/planets/list'),
  });

  if (isLoading) return <LoadingState text="Loading planets..." />;
  if (isError) return <ErrorState text="Unable to load planets" error={error} />;

  const normalized = (value) => (value || '').toString().toLowerCase();
  const filtered = data.filter((planet) => {
    const haystack = [
      planet.name,
      planet.star_system,
      planet.governed_by,
    ].map(normalized).join(' ');
    return haystack.includes(normalized(filter));
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'support_value' || sortKey === 'control_value') {
      const left = Number(a[sortKey] || 0);
      const right = Number(b[sortKey] || 0);
      if (left < right) return sortDir === 'ascending' ? -1 : 1;
      if (left > right) return sortDir === 'ascending' ? 1 : -1;
      return 0;
    }
    const left = normalized(a[sortKey]);
    const right = normalized(b[sortKey]);
    if (left < right) return sortDir === 'ascending' ? -1 : 1;
    if (left > right) return sortDir === 'ascending' ? 1 : -1;
    return 0;
  });

  const handleSort = (nextKey) => {
    if (sortKey === nextKey) {
      setSortDir(sortDir === 'ascending' ? 'descending' : 'ascending');
      return;
    }
    setSortKey(nextKey);
    setSortDir('ascending');
  };

  return (
    <>
      <Input
        icon="search"
        placeholder="Filter planets..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: '1rem' }}
      />
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              sorted={sortKey === 'name' ? sortDir : null}
              onClick={() => handleSort('name')}
            >
              Planet
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={sortKey === 'star_system' ? sortDir : null}
              onClick={() => handleSort('star_system')}
            >
              Star System
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={sortKey === 'governed_by' ? sortDir : null}
              onClick={() => handleSort('governed_by')}
            >
              Governed By
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={sortKey === 'support_value' ? sortDir : null}
              onClick={() => handleSort('support_value')}
            >
              Support
            </Table.HeaderCell>
            <Table.HeaderCell
              sorted={sortKey === 'control_value' ? sortDir : null}
              onClick={() => handleSort('control_value')}
            >
              Control
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sorted.map((planet) => (
            <Table.Row key={planet.id}>
              <Table.Cell>{planet.name}</Table.Cell>
              <Table.Cell>{planet.star_system || 'Unknown'}</Table.Cell>
              <Table.Cell>{planet.governed_by || 'Neutral'}</Table.Cell>
              <Table.Cell className="mud">
                <MudColoredText text={`&w${planet.support || ''}&w`} />
              </Table.Cell>
              <Table.Cell className="mud">
                <MudColoredText text={`&w${planet.control || ''}&w`} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

const AccountSection = () => {
  const fetchJson = useCsrfFetchJson();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['player', 'account'],
    queryFn: () => fetchJson('/player/users/current'),
  });

  if (isLoading) return <LoadingState text="Loading account..." />;
  if (isError) return <ErrorState text="Unable to load account" error={error} />;

  return (
    <div>
      <div className="portal-kicker">Account</div>
      <h3>{data?.name || 'Account'}</h3>
      <p><strong>Email:</strong> {data?.email || '-'}</p>
      <p><strong>Roles:</strong> {data?.role_names || '-'}</p>
      <p><strong>Current Character:</strong> {data?.current_character || 'None'}</p>
      {data?.id && (
        <p>
          <a className="lots-btn lots-btn--ghost" href={`/player/users/${data.id}`}>Edit account</a>
        </p>
      )}
    </div>
  );
};

const WhoSection = () => {
  const fetchJson = useCsrfFetchJson();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['player', 'who'],
    queryFn: () => fetchJson('/player/who/list'),
    refetchInterval: 60000,
  });

  if (isLoading) return <LoadingState text="Loading who list..." />;
  if (isError) return <ErrorState text="Unable to load who list" error={error} />;

  return (
    <div>
      <div className="portal-kicker">Live</div>
      <h3>Who's Online?</h3>
      <pre className="mud" dangerouslySetInnerHTML={{ __html: data?.html || '' }} />
    </div>
  );
}

const sections = [
  { key: 'who', label: "Who's online?", render: () => <WhoSection /> },
  { key: 'ooc', label: 'OOC Chat', render: () => <OocChat apiPath="/player/mud_channels/ooc_messages" /> },
  { key: 'characters', label: 'Characters', render: () => <CharactersSection /> },
  { key: 'factions', label: 'Factions', render: () => <FactionsSection /> },
  { key: 'planets', label: 'Planets', render: () => <PlanetsSection /> },
  { key: 'force', label: 'Force Apps', render: () => <ForceAppsSection /> },
  { key: 'helps', label: 'Helps', render: () => <HelpsSection /> },
  { key: 'rp_logs', label: 'RP Logs', render: () => <RpLogsSection /> },
  { key: 'rp_requests', label: 'RP Requests', render: () => <RpRequestsSection /> },
  { key: 'account', label: 'Account', render: () => <AccountSection /> },
  
];

const PortalHero = ({ currentCharacter }) => (
  <div className="lots-hero">
    <div className="portal-kicker">Command Deck</div>
    <h1 className="lots-hero__title">Legacy of the Sith</h1>
    <p className="lots-hero__subtitle">
      Manage your roster, onboard new pilots, and jump into the action fast. Everything you need to get back into LotS
      lives here.
    </p>
    <div className="portal-cta-row">
      <a className="lots-btn" href="/mud">Play Online (alpha)</a>
      <a className="lots-btn lots-btn--ghost" href="/character/new">Create Character</a>
      {currentCharacter?.name && (
        <span className="portal-chip">Active: {currentCharacter.name}</span>
      )}
    </div>
  </div>
);

const PortalGuides = () => (
  <>
    <div className="lots-card">
      <div className="portal-kicker">New to MUDs</div>
      <h4>Start with the basics</h4>
      <p>Learn commands, etiquette, and how text worlds work in under 10 minutes.</p>
      <a className="lots-btn lots-btn--ghost" href="/how-to-play">Beginner Guide</a>
    </div>
    <div className="lots-card">
      <div className="portal-kicker">New to LotS</div>
      <h4>Choose a path</h4>
      <p>Pick a faction, find a mentor, and claim your first objective quickly.</p>
      <div className="portal-note">Tip: use the Helps tab for faction primers.</div>
    </div>
    <div className="lots-card">
      <div className="portal-kicker">Returning Players</div>
      <h4>Get back in fast</h4>
      <p>Check MOTD, grab OTP, and review your last RP log for context.</p>
      <a className="lots-btn lots-btn--ghost" href="#rp_logs">Open RP Logs</a>
    </div>
    <div className="lots-card">
      <div className="portal-kicker">Veteran MUDders</div>
      <h4>Jump straight in</h4>
      <p>Copy OTP, connect, and resume with your macros. The portal keeps your essentials ready.</p>
    </div>
  </>
);

const PlayerPortalContent = () => {
  const fetchJson = useCsrfFetchJson();
  const { data: currentCharacter, isLoading: isCurrentLoading } = useQuery({
    queryKey: ['player', 'characters', 'current'],
    queryFn: () => fetchJson('/player/characters/current'),
  });

  const parseHash = (defaultKey) => {
    const raw = window.location.hash.replace('#', '');
    if (!raw) return { key: defaultKey, factionId: null, characterId: null };
    const [key, id] = raw.split('/');
    if (key === 'factions' && id) {
      return { key, factionId: Number(id) || null };
    }
    if (key === 'characters' && id) {
      return { key, characterId: Number(id) || null };
    }
    return { key, factionId: null, characterId: null };
  };
  const [{ key: active, factionId, characterId }, setRoute] = React.useState(() => parseHash(sections[0].key));
  const activeSection = sections.find((section) => section.key === active) || sections[0];
  const appliedDefaultRef = React.useRef(false);

  React.useEffect(() => {
    const handleHashChange = () => {
      setRoute(parseHash(sections[0].key));
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  React.useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (isCurrentLoading) return;
    if (window.location.hash) return;
    if (!currentCharacter?.id) {
      setRoute({ key: 'characters', factionId: null, characterId: null });
    }
    appliedDefaultRef.current = true;
  }, [currentCharacter, isCurrentLoading]);

  React.useEffect(() => {
    const nextHash = active === 'factions' && factionId
      ? `#factions/${factionId}`
      : active === 'characters' && characterId
        ? `#characters/${characterId}`
        : `#${active}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, '', nextHash);
    }
  }, [active, factionId, characterId]);

  const setActive = (nextKey) => {
    setRoute({ key: nextKey, factionId: null, characterId: null });
  };

  const setFactionId = (id) => {
    setRoute({ key: 'factions', factionId: id, characterId: null });
  };

  const setCharacterId = (id) => {
    setRoute({ key: 'characters', factionId: null, characterId: id });
  };

  const showEmptyState = !currentCharacter?.id;

  return (
    <div className="lots-shell">
      <div className="lots-container">
        <PortalHero currentCharacter={currentCharacter} />
        <div className="portal-layout">
          <nav className="portal-nav">
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                className={active === section.key ? 'active' : ''}
                onClick={() => setActive(section.key)}
              >
                {section.label}
              </button>
            ))}
          </nav>
          <div className="portal-main">
            <div className="portal-section">
              {activeSection.key === 'factions'
                ? <FactionsSection selectedId={factionId} onSelect={setFactionId} />
                : activeSection.key === 'characters'
                  ? (
                    <CharactersSection
                      selectedId={characterId}
                      onSelect={setCharacterId}
                      showEmptyState={showEmptyState}
                    />
                  )
                  : activeSection.render()}
            </div>
          </div>
          <aside className="portal-rail">
            <PortalGuides />
          </aside>
        </div>
      </div>
    </div>
  );
};

const PlayerPortal = () => {
  const queryClient = useQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <PlayerPortalContent />
    </QueryClientProvider>
  );
};

export default PlayerPortal;
